import { type KeyboardEvent, useCallback, useEffect } from "react";
import { useDropdownContext } from "../Dropdown";
import { useComboBoxContext } from "./context";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import type { IComboBoxSize } from "@/ui-kit";

export const useComboBoxTrigger = () => {
  const isMobile = useMediaQuery("(max-width: 639px)");
  const comboBoxContext = useComboBoxContext();
  const dropdownContext = useDropdownContext();

  const {
    filteredOptions,
    onSelect,
    searchValue,
    openLength = 2,
  } = comboBoxContext;
  const size: IComboBoxSize = comboBoxContext.size || (isMobile ? "lg" : "md");

  const { open, setOpen, activeIndex, setActiveIndex, contentId } =
    dropdownContext;

  const firstEnabledIndex = useCallback((opts: { disabled?: boolean }[]) => {
    return opts.findIndex((o) => !o.disabled);
  }, []);

  const activeDescId =
    activeIndex != null ? `${contentId}-option-${activeIndex}` : undefined;

  useEffect(() => {
    if (!open) return;

    if (filteredOptions.length === 0) {
      setActiveIndex(null);
      return;
    }

    if (
      activeIndex == null ||
      !filteredOptions[activeIndex] ||
      filteredOptions[activeIndex].disabled
    ) {
      const idx = firstEnabledIndex(filteredOptions);
      setActiveIndex(idx === -1 ? null : idx);
    }
  }, [open, filteredOptions, activeIndex, setActiveIndex, firstEnabledIndex]);

  const onInputKeyDownCapture = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      const key = e.key;

      if (key === "ArrowDown" || key === "ArrowUp") {
        if (!open) {
          e.preventDefault();
          setOpen(true);
          setActiveIndex(0);
          e.stopPropagation();
          return;
        }
        e.preventDefault();
        return;
      }

      if (key === "Enter") {
        if (!open || comboBoxContext.loading) return;
        const idx = activeIndex ?? (filteredOptions.length ? 0 : -1);
        if (idx < 0) return;

        const opt = filteredOptions[idx];
        if (opt && !opt.disabled) {
          e.preventDefault();
          e.stopPropagation();
          onSelect(opt);
        }
        return;
      }

      if (key === "Escape") {
        if (searchValue) {
          e.preventDefault();
        } else if (open) {
          e.preventDefault();
          setOpen(false);
        }
        return;
      }
    },
    [
      open,
      setOpen,
      setActiveIndex,
      comboBoxContext.loading,
      activeIndex,
      filteredOptions,
      onSelect,
      searchValue,
    ],
  );

  const onInputFocus = useCallback(() => {
    if (
      !comboBoxContext.disabled &&
      !open &&
      ((!searchValue && openLength === 0) || searchValue.length >= openLength)
    ) {
      setOpen(true);
    }
  }, [comboBoxContext.disabled, open, searchValue, setOpen, openLength]);

  const dataAttributes = Object.fromEntries(
    Object.entries(comboBoxContext).filter(([key]) => key.startsWith("data-")),
  );

  return {
    ...comboBoxContext,
    ...dropdownContext,
    activeDescId,
    onInputKeyDownCapture,
    onInputFocus,
    dataAttributes,
    firstEnabledIndex,
    size,
  };
};
